import React, {Fragment} from "react";
import PageHeaderLayout from "../../../../layout/pageHeaderLayout";
import withAuth from "../../../../utils/withAuth";
import {Button, Space, Popconfirm, Tag, notification, Modal} from "antd";
import useAntTable from "../../../../utils/useAntTable";
import useFormModal from "../../../../utils/useFormModal";

import * as api from "../../../../apis/platforms/admin/sysUser";
import ModifyModal from "./modifyModal";
import {useRequest} from "ahooks";
import StandardTable, {StandardColumnsType} from "../../../../components/StandardTable";
import Divider from "../../../../components/Divider";
import CompanyModal from "./companyModal";


const AddButton = withAuth('add', Button)
const EditFragment = withAuth('edit', React.Fragment)
const DeletePopconfirm = withAuth('disable', Popconfirm)
const StatusPopconfirm = withAuth('enable', Popconfirm)
const ResetPopconfirm = withAuth('password/reset', Popconfirm)
const CompanyFragment = withAuth('company', Fragment)

const statusTags: any = {
    0: <Tag color={'error'}>停用</Tag>,
    1: <Tag color={'success'}>正常</Tag>,
    2: <Tag color={'warning'}>未激活</Tag>
}

interface SysUserProps extends React.ComponentProps<any> {
    platform?: number
}

function SysUser(props: SysUserProps) {
    const {platform = 0} = props
    const {tableProps, refresh, form, search} = useAntTable(`${api.getUsers}?platform=${platform}`, {})
    const {setVisible, modalProps} = useFormModal({
        beforeRequest: values => {
            const territoryId = values.territoryId?.pop ? values.territoryId.pop() : null
            return {...values, territoryId: territoryId}
        },
        onRequest: api.modifyUser,
        onOk: (response) => {
            if (response.data.data) {
                notification["success"]({
                    message: '用户创建成功',
                    description: (
                        <span>初始密码为<span style={{color: 'red'}}>{response.data.data}</span>，首次登陆将要求重置密码</span>
                    ),
                    duration: 0
                })
            }
            setVisible(false)
            refresh()
        }
    })

    const {modalProps: companyModalProps, setVisible: setCompanyVisible} = useFormModal({
        onRequest: api.setUserCompanies,
        onOk: () => {
            setCompanyVisible(false)
        }
    })

    const {run: onDelete} = useRequest(api.deleteUser, {
        manual: true,
        onSuccess: () => {
            refresh()
        }
    });
    const {run: changeStatus} = useRequest(api.updateUserStatus, {
        manual: true,
        onSuccess: () => {
            refresh()
        }
    })
    const {run: onResetPassword} = useRequest(api.resetUserPassword, {
        manual: true,
        onSuccess: (response) => {
            notification["success"]({
                message: '密码重置成功',
                description: (
                    <span>密码已重置为<span
                        style={{color: 'red'}}>{response.data.data}</span>，首次登陆将要求重置密码</span>
                ), duration: 0
            })
            refresh()
        }
    });


    let columns: StandardColumnsType = [
        {
            title: '姓名',
            dataIndex: 'nickName',
            search: true
        },
        {
            title: '用户名',
            dataIndex: 'userName',
            search: true
        },
        ...(platform === 0 ? [
            {
                title: '部门',
                dataIndex: 'departmentName',
            },
            {
                title: '部门OA ID',
                dataIndex: 'oaDepartmentId',
            },
            {
                title: 'OA ID',
                dataIndex: 'oaId',
            },
            {
                title: '成本中心',
                dataIndex: 'costCenter',
            },
        ] : []),
        {
            title: '手机号',
            dataIndex: 'phone',
            search: true
        },
        {
            title: '邮箱',
            dataIndex: 'email',
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (status: any) => statusTags[status],
            search: {
                valueType: 'select',
                valueEnum: [
                    {value: 0, text: '停用'},
                    {value: 1, text: '正常'},
                    {value: 2, text: '未激活'}
                ]
            }
        },
        {
            title: '管理员',
            dataIndex: 'isAdmin',
            render: (isAdmin: any) => isAdmin && <Tag color={'green'}>是</Tag>,
        },
        {
            title: '操作',
            dataIndex: 'operation',
            render: (_: any, record: any) => (
                <Divider>
                    <EditFragment>
                        <a onClick={() => setVisible(true, record)}>编辑</a>
                    </EditFragment>
                    {!record.isAdmin && (
                        <CompanyFragment>
                            <a onClick={() => setCompanyVisible(true, {
                                userId: record.id,
                                companyIds: record.companyIds
                            })}>分配公司</a>
                        </CompanyFragment>
                    )}
                    {record.status === 0 && (
                        <StatusPopconfirm title={'是否启用该用户'} onConfirm={() => changeStatus(record.id, 1)}>
                            <a>{'启用'}</a>
                        </StatusPopconfirm>
                    )}
                    {record.status === 1 && (
                        <DeletePopconfirm title={'是否停用该用户'} onConfirm={() => onDelete(record.id)}>
                            <a>停用</a>
                        </DeletePopconfirm>
                    )}
                    <ResetPopconfirm title={'是否确认重置密码'} onConfirm={() => onResetPassword(record.id)}>
                        <a>重置密码</a>
                    </ResetPopconfirm>
                </Divider>
            )
        },
    ];

    if (platform === 1) {
        columns = [
            {
                title: '公司',
                dataIndex: 'companyNames',
            },
            {
                title: '客户名称',
                dataIndex: 'dealerName',
                search: true
            },
            ...columns
        ]
    }


    return (
        <PageHeaderLayout>

            <StandardTable rowKey={'id'} columns={columns} form={form} search={search}
                           toolbar={(
                               <Space style={{}}>
                                   <AddButton type={"primary"}
                                              onClick={() => setVisible(true, {platform})}>新增</AddButton>
                               </Space>
                           )} {...tableProps} />

            <ModifyModal platform={platform} {...modalProps}/>
            <CompanyModal {...companyModalProps} />
        </PageHeaderLayout>
    )
}

export default SysUser
